import Button from 'components/Button';
import Checkbox from 'components/Checkbox';
import DatePicker from 'components/DatePicker';
import Input from 'components/Input';
import { useCallback, useRef, useState } from 'react';
import usePresetStore from 'store/presetStore';
import { IEfcContextMark, IActiveTollCharger } from 'types/apiTypes';
import Select from 'components/Select';
import { Link } from 'react-router-dom';
import { klona } from 'klona/lite';
import { z as zod, ZodIssue, ZodType } from 'zod';
import { getProperty, setProperty } from 'dot-prop';
import { typeOfPanOptions } from 'utils/optionsHelper';
import classNames from 'classnames';

const versionAndValidityValidation = {
  validFrom: zod
    .string()
    .regex(
      /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d(\.\d+)?([+-][0-2]\d:[0-5]\d|Z)$/,
      'Please select a date'
    ),
  version: zod
    .string()
    .regex(/^[0-9A-Fa-f]+$/, 'Value must be a valid hexadecimal string (composed of 0-9 and A-F)'),
};

const formValidation = {
  id: zod.string(),
  contextProvider: zod.object({
    name: zod.string(),
    countryCode: zod.string(),
    providerIdentifier: zod.number().min(0, 'The value cannot be less than 0'),
  }),

  // Editable fields
  efcContextMarkVersion: zod
    .number()
    .min(0, 'The value cannot be less than 0')
    .max(255, 'The value cannot be greater than 255'),
  manufactureId: zod.number().min(0, 'The value cannot be less than 0'),
  panTotalMinLength: zod
    .number()
    .min(16, 'The value cannot be less than 16')
    .max(19, 'The value cannot be greater than 19'),
  panTotalMaxLength: zod
    .number()
    .min(16, 'The value cannot be less than 16')
    .max(19, 'The value cannot be greater than 19'),
  uniquePartOfPAN: zod
    .string()
    .min(4, 'Value must be at least 4 characters long')
    .max(10, 'Value must not exceed 10 characters')
    .regex(/^\d+$/, 'Value must consist of only numbers'),
  typeOfContract: zod
    .string()
    .regex(/^[a-zA-Z0-9]{4}$/, 'Value must consist of exactly 4 alphanumeric characters'),
  contextVersion: zod.number().min(0, 'The value cannot be less than 0'),
  equipmentClass: zod.number().min(0, 'The value cannot be less than 0'),
  typeOfPan: zod
    .number()
    .min(1, 'The value must be either 1 or 2')
    .max(2, 'The value must be either 1 or 2'),
  typeOfEfcApplication: zod
    .number()
    .min(0, 'The value cannot be less than 0')
    .max(255, 'The value cannot be greater than 255'),
  securityLevel: zod
    .number()
    .min(0, 'The value cannot be less than 0')
    .max(255, 'The value cannot be greater than 255'),
  acCrKeyReference: zod.number().min(0, 'The value cannot be less than 0'),
  authKeyReference: zod.number().min(0, 'The value cannot be less than 0'),
  versionAndValidityEntry: zod.object(versionAndValidityValidation),
  versionAndValidity: zod.object(versionAndValidityValidation),
  obuFriendlyName: zod.string(),
  obuEID: zod.number().min(0, 'The value cannot be less than 0'),

  tollChargers: zod.array(
    zod.object({
      actorCode: zod.string(),
      name: zod.string(),
      isActive: zod.boolean(),
    })
  ),
};

const zodSchema = zod.object(formValidation);

const defaultFormData: Partial<IEfcContextMark> = {
  // Fields to just pass on
  id: 'new',

  contextProvider: {
    name: 'ØresundPAY',
    countryCode: 'SE',
    providerIdentifier: 1,
  },
  // Editable fields
  versionAndValidityEntry: {
    validFrom: '',
    version: '',
  },

  versionAndValidity: {
    validFrom: '',
    version: '',
  },

  tollChargers: [],
};
interface IEfcContextMarkFormProps {
  formData?: IEfcContextMark;
  loading?: boolean;
  onSubmit?: (newData: IEfcContextMark) => void;
}

const EfcContextMarkForm = ({ formData, loading = false, onSubmit }: IEfcContextMarkFormProps) => {
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});

  const { tollChargerValues } = usePresetStore();

  const mergeTollChargers = (
    baseFormData: IEfcContextMark | undefined,
    presets: typeof tollChargerValues
  ) =>
    presets.map((tollChargerPreset) => {
      const isActive =
        baseFormData?.tollChargers?.find((tc) => tc.actorCode === tollChargerPreset.value)
          ?.isActive ?? false;
      return {
        actorCode: tollChargerPreset.value,
        name: tollChargerPreset.label,
        isActive,
      } as IActiveTollCharger;
    });

  const [prevFormData, setPrevFormData] = useState(formData);
  const [prevTollChargerValues, setPrevTollChargerValues] = useState(tollChargerValues);
  const [localFormState, setLocalFormState] = useState<Partial<IEfcContextMark>>(() =>
    klona({
      ...(formData ?? defaultFormData),
      tollChargers: mergeTollChargers(formData, tollChargerValues),
    })
  );

  if (prevFormData !== formData || prevTollChargerValues !== tollChargerValues) {
    setPrevFormData(formData);
    setPrevTollChargerValues(tollChargerValues);
    setLocalFormState(
      klona({
        ...localFormState,
        ...(formData ?? {}),
        tollChargers: mergeTollChargers(formData, tollChargerValues),
      })
    );
  }

  const typeOfPanRef = useRef<HTMLSelectElement>(null);

  const onBlurValidation = useCallback(
    (key: string, zodValidation: ZodType) => {
      const value = getProperty(localFormState, key);
      const result = zodValidation.safeParse(value);
      const newErrorMessages = { ...errorMessages };
      if (!result.success) {
        // Add error message
        newErrorMessages[key] = result.error.issues[0].message;
      } else {
        // remove error message
        delete newErrorMessages[key];
      }
      setErrorMessages(newErrorMessages);
    },
    [errorMessages, localFormState]
  );

  const onChangeInput = (
    event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const name: string = event.target.name;
    const value: string = event.target.value;
    const newLocalFormState = klona(localFormState);
    setProperty(newLocalFormState, name, value);
    setLocalFormState(newLocalFormState);
  };

  const onChangeInputNumber = (
    event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const name: string = event.target.name;
    const value: number | string = /^\d+$/.test(event.target.value)
      ? parseInt(event.target.value)
      : event.target.value;
    const newLocalFormState = klona(localFormState);
    setProperty(newLocalFormState, name, value === '' ? undefined : value);
    setLocalFormState(newLocalFormState);
  };

  const onChangeDatePickerFilter = (newDate: Date | undefined, name: string) => {
    const newLocalFormState = klona(localFormState);
    setProperty(newLocalFormState, name, newDate?.toISOString());
    setLocalFormState(newLocalFormState);
  };

  const formatZodErrors = (zodErrors: ZodIssue[]) => {
    const newErrors: Record<string, string> = {};

    zodErrors.forEach((zodError) => {
      const path = zodError.path
        .map((path) => {
          if (typeof path === 'number') {
            return `[${path}]`;
          }
          return path;
        })
        .join('.')
        .replace(/\.\[/g, '[');

      newErrors[path] = zodError.message;
    });
    console.log('newErrors', newErrors);
    setErrorMessages(newErrors);
  };

  const onSubmitForm = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      // Validate form
      const safeParsedZod = zodSchema.safeParse(localFormState);

      if (!safeParsedZod.success) {
        formatZodErrors(safeParsedZod.error.issues);
      }

      if (safeParsedZod.success && onSubmit) {
        onSubmit(localFormState as IEfcContextMark);
      }
    },
    [localFormState, onSubmit]
  );

  return (
    <form onSubmit={onSubmitForm} className="max-w-[800px]">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <h3 className="col-span-full pt-4 pb-1 text-xl font-semibold">EFC Context Mark</h3>
        <Select
          label="contextProvider"
          options={[{ label: 'ØresundPAY (SE-1)', value: '' }]}
          disabled
        />
        <Input
          label="typeOfContract"
          name="typeOfContract"
          value={localFormState.typeOfContract}
          onChange={onChangeInput}
          error={errorMessages['typeOfContract']}
          onBlur={() => onBlurValidation('typeOfContract', formValidation.typeOfContract)}
        />
        <Input
          label="contextVersion"
          name="contextVersion"
          value={localFormState.contextVersion}
          onChange={onChangeInputNumber}
          error={errorMessages['contextVersion']}
          onBlur={() => onBlurValidation('contextVersion', formValidation.contextVersion)}
          type="number"
        />
        <Input
          label="efcContextMarkVersion"
          name="efcContextMarkVersion"
          value={localFormState.efcContextMarkVersion}
          onChange={onChangeInputNumber}
          error={errorMessages['efcContextMarkVersion']}
          onBlur={() =>
            onBlurValidation('efcContextMarkVersion', formValidation.efcContextMarkVersion)
          }
          type="number"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <h3 className="col-span-full pt-4 pb-1 text-xl font-semibold">PAN</h3>
        <Input
          label="panTotalMinLength"
          name="panTotalMinLength"
          value={localFormState.panTotalMinLength}
          onChange={onChangeInputNumber}
          error={errorMessages['panTotalMinLength']}
          onBlur={() => onBlurValidation('panTotalMinLength', formValidation.panTotalMinLength)}
          type="number"
        />
        <Input
          label="panTotalMaxLength"
          name="panTotalMaxLength"
          value={localFormState.panTotalMaxLength}
          onChange={onChangeInputNumber}
          error={errorMessages['panTotalMaxLength']}
          onBlur={() => onBlurValidation('panTotalMaxLength', formValidation.panTotalMaxLength)}
          type="number"
        />
        <Input
          label="uniquePartOfPAN"
          name="uniquePartOfPAN"
          value={localFormState.uniquePartOfPAN}
          onChange={onChangeInput}
          error={errorMessages['uniquePartOfPAN']}
          onBlur={() => onBlurValidation('uniquePartOfPAN', formValidation.uniquePartOfPAN)}
        />
        <Select
          inputClassName={classNames(!localFormState.typeOfPan && 'text-neutral-500')}
          value={localFormState.typeOfPan ?? ''}
          label="typeOfPan"
          name="typeOfPan"
          options={[{ label: '-- Select value --', value: '' }, ...typeOfPanOptions]}
          ref={typeOfPanRef}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
            onChangeInputNumber(event);
            setTimeout(() => {
              typeOfPanRef.current?.blur();
            }, 0);
          }}
          error={errorMessages['typeOfPan']}
          onBlur={() => onBlurValidation('typeOfPan', formValidation.typeOfPan)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <h3 className="col-span-full pt-4 pb-1 text-xl font-semibold">OBU</h3>
        <Input
          label="equipmentClass"
          name="equipmentClass"
          value={localFormState.equipmentClass}
          onChange={onChangeInputNumber}
          error={errorMessages['equipmentClass']}
          onBlur={() => onBlurValidation('equipmentClass', formValidation.equipmentClass)}
          type="number"
        />
        <Input
          label="manufactureId"
          name="manufactureId"
          value={localFormState.manufactureId}
          onChange={onChangeInputNumber}
          error={errorMessages['manufactureId']}
          onBlur={() => onBlurValidation('manufactureId', formValidation.manufactureId)}
          type="number"
        />
        <Input
          label="obuFriendlyName"
          name="obuFriendlyName"
          value={localFormState.obuFriendlyName}
          onChange={onChangeInput}
          error={errorMessages['obuFriendlyName']}
          onBlur={() => onBlurValidation('obuFriendlyName', formValidation.obuFriendlyName)}
        />
        <Input
          label="obuEID"
          name="obuEID"
          value={localFormState.obuEID}
          onChange={onChangeInputNumber}
          error={errorMessages['obuEID']}
          onBlur={() => onBlurValidation('obuEID', formValidation.obuEID)}
          type="number"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <h3 className="col-span-full pt-4 pb-1 text-xl font-semibold">Security</h3>
        <Input
          label="typeOfEfcApplication"
          name="typeOfEfcApplication"
          value={localFormState.typeOfEfcApplication}
          onChange={onChangeInputNumber}
          error={errorMessages['typeOfEfcApplication']}
          onBlur={() =>
            onBlurValidation('typeOfEfcApplication', formValidation.typeOfEfcApplication)
          }
          type="number"
        />
        <Input
          label="securityLevel"
          name="securityLevel"
          value={localFormState.securityLevel}
          onChange={onChangeInputNumber}
          error={errorMessages['securityLevel']}
          onBlur={() => onBlurValidation('securityLevel', formValidation.securityLevel)}
          type="number"
        />
        <Input
          label="acCrKeyReference"
          name="acCrKeyReference"
          value={localFormState.acCrKeyReference}
          onChange={onChangeInputNumber}
          error={errorMessages['acCrKeyReference']}
          onBlur={() => onBlurValidation('acCrKeyReference', formValidation.acCrKeyReference)}
          type="number"
        />
        <Input
          label="authKeyReference"
          name="authKeyReference"
          value={localFormState.authKeyReference}
          onChange={onChangeInputNumber}
          error={errorMessages['authKeyReference']}
          onBlur={() => onBlurValidation('authKeyReference', formValidation.authKeyReference)}
          type="number"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <h3 className="col-span-full pt-4 pb-1 text-xl font-semibold">versionAndValidityEntry</h3>
        <DatePicker
          label="from"
          name="versionAndValidityEntry.validFrom"
          value={
            localFormState.versionAndValidityEntry?.validFrom
              ? new Date(localFormState.versionAndValidityEntry.validFrom)
              : undefined
          }
          onChange={onChangeDatePickerFilter}
          error={errorMessages['versionAndValidityEntry.validFrom']}
          onBlur={() => {
            onBlurValidation(
              'versionAndValidityEntry.validFrom',
              versionAndValidityValidation.validFrom
            );
          }}
        />
        <Input
          label="version"
          name="versionAndValidityEntry.version"
          value={localFormState.versionAndValidityEntry?.version}
          onChange={onChangeInput}
          error={errorMessages['versionAndValidityEntry.version']}
          onBlur={() =>
            onBlurValidation(
              'versionAndValidityEntry.version',
              versionAndValidityValidation.version
            )
          }
        />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <h3 className="col-span-full pt-4 pb-1 text-xl font-semibold">versionAndValidity</h3>
        <DatePicker
          label="from"
          name="versionAndValidity.validFrom"
          value={
            localFormState.versionAndValidity?.validFrom
              ? new Date(localFormState.versionAndValidity.validFrom)
              : undefined
          }
          onChange={onChangeDatePickerFilter}
          error={errorMessages['versionAndValidity.validFrom']}
          onBlur={() => {
            onBlurValidation(
              'versionAndValidity.validFrom',
              versionAndValidityValidation.validFrom
            );
          }}
        />
        <Input
          label="version"
          name="versionAndValidity.version"
          value={localFormState.versionAndValidity?.version}
          onChange={onChangeInput}
          error={errorMessages['versionAndValidity.version']}
          onBlur={() =>
            onBlurValidation('versionAndValidity.version', versionAndValidityValidation.version)
          }
        />
      </div>
      <div>
        <h3 className="col-span-full pt-4 pb-1 text-xl font-semibold">Toll Chargers</h3>
        <div className="grid grid-flow-col gap-4">
          {localFormState.tollChargers?.map((tollCharger) => {
            return (
              <Checkbox
                key={tollCharger.actorCode}
                checked={tollCharger.isActive}
                label={tollCharger.name}
                className="rounded-md border-2 border-neutral-500 p-2"
                onChange={() => {
                  const newLocalSelectedTollChargers = localFormState.tollChargers?.map((tc) => {
                    return {
                      ...tc,
                      isActive:
                        tc.actorCode === tollCharger.actorCode
                          ? !tollCharger.isActive
                          : tc.isActive,
                    } as IActiveTollCharger;
                  });
                  setLocalFormState((prev) =>
                    klona({ ...prev, tollChargers: newLocalSelectedTollChargers })
                  );
                }}
              />
            );
          })}
        </div>
      </div>
      <div className="flex justify-between py-8">
        <Link to="./..">
          <Button variant="ghost" type="button" disabled={loading}>
            Cancel
          </Button>
        </Link>
        <Button variant="primary" type="submit" spinner={loading}>
          Save
        </Button>
      </div>
    </form>
  );
};

export default EfcContextMarkForm;
